import { useMemo } from 'react';

import { useServiceTypeTemplateCtx } from '@/app/(app)/settings/service_types/[id]/provider';
import { ToggleButtonWithCheckbox } from '@/components/atoms/toggle-button';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';

export function ServiceTypeTemplateSkills() {
  const { serviceTypeTemplate, update } = useServiceTypeTemplateCtx();
  const {
    office: { skills },
  } = useOfficeCtx();

  const currentSkillIds = serviceTypeTemplate.skill_ids;
  const allSelected = useMemo(() => currentSkillIds.length === skills.length, [currentSkillIds, skills]);

  const toggleSkill = (skillId: string) => {
    const newSkillIds = currentSkillIds.includes(skillId)
      ? currentSkillIds.filter((id) => id !== skillId)
      : [...currentSkillIds, skillId];
    update({ skill_ids: newSkillIds });
  };

  const toggleAll = () => {
    if (allSelected) {
      update({ skill_ids: [] });
    } else {
      update({ skill_ids: skills.map((s) => s.id) });
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex flex-col gap-1'>
          <h2 className='text-heading-md'>Required Skills</h2>
          <p className='text-sm text-muted-foreground'>Configure which skills are required for this service type.</p>
        </div>
        <Button variant='link' size='sm' onClick={toggleAll}>
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      {skills.length === 0 ? (
        <div className='py-8 text-center text-muted-foreground'>
          <div className='mb-2 text-lg font-medium'>No Skills Available</div>
          <p className='text-sm'>
            No skills have been configured for this office. Create skills first to assign them to service types.
          </p>
        </div>
      ) : (
        <div className='grid grid-cols-1 gap-3 gap-y-4 md:grid-cols-2'>
          {skills.map((skill) => (
            <ToggleButtonWithCheckbox
              key={skill.id}
              selected={currentSkillIds.includes(skill.id)}
              toggle={() => toggleSkill(skill.id)}
            >
              <div className='flex w-full flex-col space-y-0.5 text-left'>
                <div className='text-sm font-medium'>{skill.name}</div>
                {skill.description && (
                  <div className='text-xs font-normal text-muted-foreground'>{skill.description}</div>
                )}
              </div>
            </ToggleButtonWithCheckbox>
          ))}
        </div>
      )}
    </div>
  );
}
